import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import {
    DATETIME_CONTRAST_SHADOW_STYLE_ATTRIBUTE,
    DATETIME_CONTRAST_SHADOW_STYLE_CSS,
    DATETIME_CONTRAST_SHADOW_STYLE_SELECTOR,
    DATETIME_HOST_HIDE_TIME_CLASS,
    DATETIME_PRESENTATION_DATE_TIME,
    DATETIME_SHADOW_STYLE_ATTRIBUTE,
    DATETIME_SHADOW_STYLE_CSS,
    DATETIME_SHADOW_STYLE_SELECTOR
} from './datetime-picker.constants';
import { DatetimePickerUtils } from './datetime-picker.utils';
import { ApplicationFacade } from '../application/+state/application.facade';
import Env from '@ridango/core/env';

/**
 * Thin Angular wrapper around Ionic `ion-datetime`.
 */
@Component({
    selector: 'ridango-datetime-picker',
    templateUrl: './datetime-picker.component.html',
    styleUrls: ['./datetime-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatetimePickerComponent),
            multi: true
        }
    ]
})
export class DatetimePickerComponent extends BaseComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
    @HostBinding(DATETIME_HOST_HIDE_TIME_CLASS) hideTime = false;
    @Input() min?: string;
    @Input() max?: string;
    @Input() firstDayOfWeek = 1;
    @Input() locale?: string;
    @Input() color?: string;
    @Input() hourCycle?: string;
    @Input() size?: 'cover' | 'fixed' | string;
    @Input() showDefaultButtons = false;
    @Input() closeOnSelect = false;
    @Input() set forceNativeDatePresentation(value: boolean) {
        this.forceNativeDatePresentationRequested = value === true;
        this.applyPresentationState(this.requestedPresentationMode);
    }
    @Input() set presentation(mode: string) {
        this.applyPresentationState(mode);
    }

    @Output() valueChange = new EventEmitter<string | null>();
    @Output() ionFocus = new EventEmitter<Event>();
    @Output() requestClose = new EventEmitter<void>();

    @ViewChild('datetimePicker', { read: ElementRef }) datetimePicker?: ElementRef<HTMLElement>;

    value: string | null = null;
    disabled = false;
    presentationMode = DATETIME_PRESENTATION_DATE_TIME;
    private requestedPresentationMode = DATETIME_PRESENTATION_DATE_TIME;
    private isTimeNeeded = false;
    private hideTimeRequested = false;
    private forceNativeDatePresentationRequested = false;
    private contrastStylesSubscriptionInitialized = false;
    private contrastWheelObserver?: MutationObserver;
    private wheelPickerStylesObserver?: MutationObserver;
    private wheelAccessibilityRootObserver?: MutationObserver;
    // Each wheel column owns its own Shadow DOM, so accessibility labels must be
    // refreshed per column as items change.
    private wheelColumnAccessibilityObservers: MutationObserver[] = [];
    private wheelAccessibilityShadowRoot?: ShadowRoot;
    constructor(
        @Inject('ENV') public env: Env,
        private appFacade: ApplicationFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.applyPresentationState(this.requestedPresentationMode);
        this.applyContrastShadowStyles();
    }

    ngOnDestroy(): void {
        this.teardownContrastWheelObserver();
        this.teardownWheelPickerStyles();
        this.teardownWheelAccessibility();
        super.ngOnDestroy();
    }

    get normalizedValue(): string | null {
        const sanitized = DatetimePickerUtils.sanitizeInputValue(
            this.value,
            this.shouldNormalizeDateOnlyInputs,
            'start'
        );
        return sanitized ?? null;
    }

    get normalizedMin(): string | undefined {
        return (
            DatetimePickerUtils.sanitizeInputValue(this.min, this.shouldNormalizeDateOnlyInputs, 'start') ?? undefined
        );
    }

    get normalizedMax(): string | undefined {
        return DatetimePickerUtils.sanitizeInputValue(this.max, this.shouldNormalizeDateOnlyInputs, 'end') ?? undefined;
    }

    get canRenderDatetime(): boolean {
        return !!this.presentationMode && DatetimePickerUtils.areBoundsValid(this.normalizedMin, this.normalizedMax);
    }

    writeValue(value: string | null): void {
        this.value = value;
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    handleIonChange(event: Event): void {
        const { normalizedValue, isUserEvent } = DatetimePickerUtils.normalizeIonChangeEvent(event);
        const hasChanged = this.value !== normalizedValue;

        if (!hasChanged) {
            return;
        }

        this.value = normalizedValue;
        this.onChange(normalizedValue);
        this.valueChange.emit(normalizedValue);

        if (this.closeOnSelect && isUserEvent) {
            this.requestClose.emit();
        }
    }

    handleIonFocus(event: Event): void {
        this.ionFocus.emit(event);
    }

    handleIonBlur(): void {
        this.onTouched();
    }

    /**
     * Toggles Shadow DOM styles inside Ionic `ion-datetime` to hide time-related picker columns.
     */
    private applyShadowStyles(hideTime = false): void {
        // If component had date-time, skip everything that is bellow.
        if (this.isTimeNeeded) {
            return;
        }

        this.hideTime = hideTime;
        const datetimeElement = this.datetimePicker?.nativeElement;
        if (!datetimeElement) {
            return;
        }
        DatetimePickerUtils.applyShadowStyle(
            datetimeElement,
            hideTime,
            DATETIME_SHADOW_STYLE_SELECTOR,
            DATETIME_SHADOW_STYLE_ATTRIBUTE,
            DATETIME_SHADOW_STYLE_CSS
        );
    }

    /**
     * Keeps wheel highlight colors in sync with high-contrast theme.
     */
    private applyContrastShadowStyles(): void {
        if (this.contrastStylesSubscriptionInitialized) {
            this.syncContrastWheelStyles();
            return;
        }

        this.contrastStylesSubscriptionInitialized = true;
        this.appFacade.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isDarkTheme) => {
            const datetimeElement = this.datetimePicker?.nativeElement;
            if (!datetimeElement) {
                return;
            }

            if (!isDarkTheme) {
                this.teardownContrastWheelObserver();
                this.syncContrastWheelStyles();
                return;
            }

            DatetimePickerUtils.applyShadowStyle(
                datetimeElement,
                true,
                DATETIME_CONTRAST_SHADOW_STYLE_SELECTOR,
                DATETIME_CONTRAST_SHADOW_STYLE_ATTRIBUTE,
                DATETIME_CONTRAST_SHADOW_STYLE_CSS
            );

            this.setupContrastWheelObserver(datetimeElement);
            this.syncContrastWheelStyles();
            requestAnimationFrame(() => this.syncContrastWheelStyles());
        });
    }

    private syncContrastWheelStyles(): void {
        DatetimePickerUtils.syncContrastWheelStyles(this.datetimePicker?.nativeElement);
    }

    private setupContrastWheelObserver(datetimeElement: HTMLElement): void {
        DatetimePickerUtils.runWhenShadowReady(datetimeElement, (shadowRoot) => {
            this.teardownContrastWheelObserver();
            this.contrastWheelObserver = new MutationObserver(() => this.syncContrastWheelStyles());
            this.contrastWheelObserver.observe(shadowRoot, { childList: true, subtree: true });
        });
    }

    private teardownContrastWheelObserver(): void {
        this.contrastWheelObserver?.disconnect();
        this.contrastWheelObserver = undefined;
    }

    private setupWheelPickerStyles(): void {
        const datetimeElement = this.datetimePicker?.nativeElement;
        const wheelEnabled = Boolean(datetimeElement) && this.env?.ENABLE_WHEEL_DATE_TIME_PICKER === true;

        if (!wheelEnabled || !datetimeElement) {
            this.teardownWheelPickerStyles();
            return;
        }

        DatetimePickerUtils.runWhenShadowReady(datetimeElement, (shadowRoot) => {
            this.teardownWheelPickerStyles();
            this.wheelPickerStylesObserver = new MutationObserver(() =>
                DatetimePickerUtils.syncWheelPickerStyles(datetimeElement)
            );
            this.wheelPickerStylesObserver.observe(shadowRoot, { childList: true, subtree: true });
            DatetimePickerUtils.syncWheelPickerStyles(datetimeElement);
            requestAnimationFrame(() => DatetimePickerUtils.syncWheelPickerStyles(datetimeElement));
        });
    }

    private teardownWheelPickerStyles(): void {
        this.wheelPickerStylesObserver?.disconnect();
        this.wheelPickerStylesObserver = undefined;
    }

    private applyPresentationState(mode: string): void {
        this.requestedPresentationMode = mode;
        const state = DatetimePickerUtils.resolvePresentationState(
            mode,
            this.env,
            this.forceNativeDatePresentationRequested
        );
        this.presentationMode = state.presentationMode;
        this.isTimeNeeded = state.isTimeNeeded;
        this.hideTimeRequested = state.hideTime;
        this.applyShadowStyles(this.hideTimeRequested);
        this.setupWheelPickerStyles();
        this.setupWheelAccessibility();
    }

    private setupWheelAccessibility(): void {
        const datetimeElement = this.datetimePicker?.nativeElement;
        const wheelEnabled =
            Boolean(datetimeElement) &&
            this.env?.ENABLE_WHEEL_DATE_TIME_PICKER === true &&
            this.presentationMode === DATETIME_PRESENTATION_DATE_TIME;

        if (!wheelEnabled) {
            this.teardownWheelAccessibility();
            return;
        }

        DatetimePickerUtils.runWhenShadowReady(datetimeElement, (shadowRoot) => {
            if (this.wheelAccessibilityShadowRoot !== shadowRoot) {
                this.teardownWheelAccessibility();
                this.wheelAccessibilityShadowRoot = shadowRoot;
                // Wheel items are rendered lazily inside Shadow DOM, so labels must be resynced on subtree changes.
                this.wheelAccessibilityRootObserver = new MutationObserver(() => this.syncWheelAccessibility());
                this.wheelAccessibilityRootObserver.observe(shadowRoot, { childList: true, subtree: true });
            }

            this.syncWheelAccessibility();
        });
    }

    private teardownWheelAccessibility(): void {
        this.wheelAccessibilityRootObserver?.disconnect();
        this.wheelAccessibilityRootObserver = undefined;
        this.wheelColumnAccessibilityObservers.forEach((observer) => observer.disconnect());
        this.wheelColumnAccessibilityObservers = [];
        this.wheelAccessibilityShadowRoot = undefined;
    }

    private syncWheelAccessibility(): void {
        if (!this.wheelAccessibilityShadowRoot) {
            return;
        }

        DatetimePickerUtils.syncWheelAccessibility(this.wheelAccessibilityShadowRoot, {
            date: this.translate.instant('LOCAL_LABEL_DATE'),
            hours: this.translate.instant('HOURS'),
            minutes: this.translate.instant('MINUTES')
        });

        this.observeWheelColumnAccessibility();
    }

    private observeWheelColumnAccessibility(): void {
        this.wheelColumnAccessibilityObservers.forEach((observer) => observer.disconnect());
        this.wheelColumnAccessibilityObservers = [];

        DatetimePickerUtils.getWheelColumns(this.wheelAccessibilityShadowRoot).forEach((column) => {
            if (!column.shadowRoot) {
                return;
            }

            // Active picker items toggle classes and text inside nested Shadow DOM as the wheel scrolls.
            const observer = new MutationObserver(() => this.syncWheelAccessibility());
            observer.observe(column.shadowRoot, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
                attributeFilter: ['class']
            });
            this.wheelColumnAccessibilityObservers.push(observer);
        });
    }

    private onChange: (value: string | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    private get shouldNormalizeDateOnlyInputs(): boolean {
        return !this.isTimeNeeded && this.presentationMode === DATETIME_PRESENTATION_DATE_TIME;
    }
}
